import React, { useCallback, useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import CircularProgress from '@mui/material/CircularProgress';
import GreenSubmitButton from '../../buttons/greenSubmitButton';
import loadGoogleMaps from '../../utils/mapsLoader';
import {
  validCoordinates, distanceKm, extractAddress, matchTenantCity, MAX_DELIVERY_KM
} from '../../utils/geo';
import _useTranslate from '../../hooks/_useTranslate';
import './AddressPinPicker.css';

/* =====================================================================
   AddressPinPicker — "drag the pin to your exact door"
   ---------------------------------------------------------------------
   The calibration step: an autocompleted address gets you to the right
   building, this gets you to the right entrance. Ported from the admin's
   own implementation (dashboard orderView.component.ts: gmpDraggable
   marker -> dragend -> reverse geocode -> updateOrderAddress), which
   writes the same latitude/longitude the courier app and DeliverasNow
   already read off the order.

   GATED ON canShowMap() — i.e. IS_GEO *and* a tenant key — because this
   renders map TILES, which are metered. canGeocode() alone is not enough:
   that gate is for text lookups, this one costs tile loads.

   COST: the reverse geocode fires ONLY when the customer presses
   "Update address", never on drag. Dragging is free; each drop would
   otherwise be a billable lookup.

   SAFETY: the same >MAX_DELIVERY_KM reject the autocomplete uses. A pin
   can be dragged somewhere absurd just as easily as a bad geocode can
   land there.
   ===================================================================== */

const AddressPinPicker = ({
  feathersStore,
  latitude,
  longitude,
  onConfirm,
  compact = false
}) => {

  const common = _useTranslate(feathersStore.language, 'common');

  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapsRef = useRef(null);
  const pendingRef = useRef(null);      // position after a drag, before confirm
  const mountedRef = useRef(true);

  const [ready, setReady] = useState(false);
  const [moved, setMoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const settings = feathersStore.settings || {};
  const hasStart = validCoordinates(latitude, longitude);
  // Fall back to the venue so the customer still gets a usable map when the
  // address has no coordinates yet (typed by hand, or an older saved address).
  const start = hasStart
    ? { lat: Number(latitude), lng: Number(longitude) }
    : (validCoordinates(settings.latitude, settings.longitude)
        ? { lat: Number(settings.latitude), lng: Number(settings.longitude) }
        : null);

  const enabled = feathersStore.canShowMap() && !!start;

  useEffect(() => () => { mountedRef.current = false; }, []);

  // Build the map once, then keep the marker in step with the incoming props.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const maps = await loadGoogleMaps(feathersStore.tenantMapsKey());
        if (!maps || cancelled || !mapNodeRef.current) return;
        mapsRef.current = maps;
        if (!mapRef.current) {
          mapRef.current = new maps.Map(mapNodeRef.current, {
            center: start,
            zoom: 17,                 // door-level, this is a calibration step
            disableDefaultUI: true,
            zoomControl: true,
            gestureHandling: 'greedy' // one-finger pan on mobile inside the form
          });
          // Classic Marker on purpose: AdvancedMarkerElement needs a Map ID,
          // which tenants would have to create. draggable works on both.
          markerRef.current = new maps.Marker({
            map: mapRef.current,
            position: start,
            draggable: true
          });
          markerRef.current.addListener('dragend', () => {
            const p = markerRef.current.getPosition();
            pendingRef.current = { lat: p.lat(), lng: p.lng() };
            if (mountedRef.current) { setMoved(true); setNotice(''); }
          });
        } else {
          mapRef.current.panTo(start);
          markerRef.current.setPosition(start);
        }
        if (mountedRef.current) setReady(true);
      } catch (error) {
        console.log('pin picker map failed to load', error?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, start?.lat, start?.lng]);   // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = useCallback(async () => {
    const target = pendingRef.current;
    if (!target || !mapsRef.current) return;
    try {
      setBusy(true);
      setNotice('');

      // The bias/short-list is a hint; THIS is the guarantee.
      if (validCoordinates(settings.latitude, settings.longitude)) {
        const km = distanceKm(target.lat, target.lng, settings.latitude, settings.longitude);
        if (km > MAX_DELIVERY_KM) {
          setNotice(common.addressTooFar || 'That address looks too far from the shop — please check it.');
          return;
        }
      }

      // ONE billable lookup, only now.
      const geocoder = new mapsRef.current.Geocoder();
      const { results } = await geocoder.geocode({ location: target });
      const parsed = results?.[0] ? extractAddress(results[0].address_components) : {};
      const matched = matchTenantCity(
        parsed.cityCandidates, settings.citys,
        (c) => feathersStore._translate(c.name, c.nameEnglish));

      onConfirm && onConfirm({
        street: parsed.street || '',
        streetNumber: parsed.streetNumber || '',
        postcode: parsed.postcode || '',
        cityId: matched ? matched.id : null,
        cityLabel: matched ? matched.label : '',
        latitude: target.lat,
        longitude: target.lng,
        formattedAddress: results?.[0]?.formatted_address || ''
      });
      if (mountedRef.current) {
        setMoved(false);
        setNotice(common.addressPinSaved || 'Location updated.');
      }
    } catch (error) {
      console.log('pin reverse geocode failed', error?.message);
      setNotice(common.addressLookupFailed || 'Could not read that address, please type it.');
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  }, [settings, common, onConfirm, feathersStore]);

  if (!enabled) return null;

  return (
    <div className={`pin-picker${compact ? ' pin-picker--compact' : ''}`}>
      <div className="pin-picker-hint">
        {common.adjustPinHint || 'Drag the pin to your exact entrance'}
      </div>
      {/* The map div MUST have no React children: Google replaces its contents,
          so a React-owned child (e.g. a spinner) later fails to unmount with
          "removeChild: The node to be removed is not a child of this node" and
          takes the page down. Overlay the spinner as a SIBLING instead. */}
      <div className="pin-picker-map-wrap">
        <div className="pin-picker-map" ref={mapNodeRef} />
        {!ready &&
          <div className="pin-picker-loading"><CircularProgress size={22} color="secondary" /></div>}
      </div>
      {notice ? <div className="pin-picker-notice">{notice}</div> : null}
      <GreenSubmitButton
        title={common.updateAddress || 'Update address'}
        onClick={confirm}
        disabled={!moved || busy}
        loading={busy}
        width="100%"
        size="small"
        marginTop={0.5}
      />
    </div>
  );
};

export default inject('feathersStore')(observer(AddressPinPicker));
